import React from "react";
import PropTypes from "prop-types";
import { PLAN_STATUS } from "../../constants/plans";
import { formatDateWithTime } from "../../utils/formatters";
import { PlanPrice } from "../billing";

// M5: shouldShow determines badge visibility using plan data, replacing the
// ambiguous paidOnly boolean. Each status declares its own display logic.
const STATUS_BADGES = {
  [PLAN_STATUS.ACTIVE]: {
    label: "Active",
    cssClass: "billing-status-active",
    role: "status",
    ariaLabel: "Subscription status: Active",
    title: "Your subscription is active",
    // Hide for free plans (price === 0); show for paid (price > 0) and custom/enterprise (price === null)
    shouldShow: (plan) => !!plan && plan.price !== 0,
  },
  [PLAN_STATUS.CANCELED]: {
    label: "Canceling",
    cssClass: "billing-status-canceled",
    role: "status",
    ariaLabel: "Subscription status: Canceling at end of billing period",
    title: "Your subscription will end at the current billing period",
  },
  [PLAN_STATUS.PAST_DUE]: {
    label: "Past Due",
    cssClass: "billing-status-past-due",
    role: "alert",
    ariaLabel: "Subscription status: Past due - payment required",
    title:
      "Payment failed. Please update your payment method to avoid service interruption.",
  },
  [PLAN_STATUS.UNPAID]: {
    label: "Unpaid",
    cssClass: "billing-status-unpaid",
    role: "alert",
    ariaLabel: "Subscription status: Unpaid - payment overdue",
    title: "Payment is overdue. Service may be limited.",
  },
  [PLAN_STATUS.TRIALING]: {
    label: "Trial",
    cssClass: "billing-status-trialing",
    role: "status",
    ariaLabel: "Subscription status: Free trial active",
    title: "You're on a free trial",
  },
  [PLAN_STATUS.INCOMPLETE]: {
    label: "Incomplete",
    cssClass: "billing-status-incomplete",
    role: "alert",
    ariaLabel: "Subscription status: Incomplete - payment setup required",
    title: "Payment setup incomplete. Please complete your payment.",
  },
  [PLAN_STATUS.PAUSED]: {
    label: "Paused",
    cssClass: "billing-status-paused",
    role: "status",
    ariaLabel: "Subscription status: Paused",
    title: "Your subscription is paused",
  },
  [PLAN_STATUS.INCOMPLETE_EXPIRED]: {
    label: "Expired",
    cssClass: "billing-status-incomplete",
    role: "alert",
    ariaLabel: "Subscription status: Expired - payment was not completed",
    title:
      "Payment was not completed in time. Please start a new subscription.",
  },
};

/**
 * Displays the user's current plan with status badges and manage button.
 */
const CurrentPlanCard = ({
  currentPlan,
  currentPlanId,
  planStatus,
  planExpiresAt,
  openingPortal,
  onManageSubscription,
}) => {
  const hasOrHadPaidSubscription =
    currentPlan?.price > 0 ||
    planStatus === PLAN_STATUS.CANCELED ||
    planStatus === PLAN_STATUS.PAST_DUE;

  const badge = STATUS_BADGES[planStatus];
  // M5: shouldShow defaults to true (always visible) when not specified
  const showBadge =
    badge && (badge.shouldShow ? badge.shouldShow(currentPlan) : true);

  return (
    <div className="settings-card billing-current-plan">
      <div className="billing-plan-header">
        <div>
          <h3 className="settings-card-title">Current Plan</h3>
          <p className="billing-plan-name">
            {currentPlan?.name || "Free"}
            {showBadge && (
              <span
                className={`billing-status-badge ${badge.cssClass}`}
                role={badge.role}
                aria-label={badge.ariaLabel}
                title={badge.title}
              >
                {badge.label}
              </span>
            )}
          </p>
          {planStatus === PLAN_STATUS.CANCELED && planExpiresAt && (
            <p className="billing-plan-expires">
              Access until {formatDateWithTime(planExpiresAt)}
            </p>
          )}
          {planStatus === PLAN_STATUS.PAST_DUE && (
            <p className="billing-status-help billing-status-help-past-due">
              Please update your payment method to continue your subscription.
            </p>
          )}
        </div>
        <div className="billing-plan-price">
          <PlanPrice plan={currentPlan} />
        </div>
      </div>

      {/* Manage Subscription Button — show for anyone who has (or had) a paid subscription,
          including grandfathered users on deactivated plans */}
      {hasOrHadPaidSubscription && (
        <button
          className="billing-manage-btn"
          onClick={onManageSubscription}
          disabled={openingPortal}
        >
          {openingPortal ? "Opening..." : "Manage Subscription"}
        </button>
      )}
    </div>
  );
};

CurrentPlanCard.propTypes = {
  currentPlan: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    price: PropTypes.number,
    features: PropTypes.arrayOf(PropTypes.string),
  }),
  currentPlanId: PropTypes.string,
  planStatus: PropTypes.string,
  planExpiresAt: PropTypes.string,
  openingPortal: PropTypes.bool,
  onManageSubscription: PropTypes.func.isRequired,
};

export default CurrentPlanCard;
